import React, { useEffect, useRef } from "react";

import { z } from "zod";

import { Icon } from "@braze/beacon-core";

import { useAgenticAiContext } from "@services/useAgenticAiContext";
import { useCanUseOperator } from "@services/useCanUseOperator";
import useI18nNamespace from "@services/useI18nNamespace";
import { useIsAgenticAiChatOpen } from "@services/useIsAgenticAiChatOpen";
import { useOperatorEntityId } from "@services/useOperatorEntityId";
import { useRegisterChangeTool } from "@services/useRegisterChangeTool";

import { DiffDisplay } from "@components/AgenticAi/components/DiffDisplay/DiffDisplay";

import { StyledBrazeModal } from "./BrazeModal.style";
import type { BrazeModalProps } from "./types";

export const BrazeModal = ({
  isOpen,
  onOperatorClose,
  operatorLabel,
  operatorDisabledReason,
  children,
  size = "sm",
  ignoreOutsideClick,
  toggle,
  hideCloseButton,
  ...props
}: BrazeModalProps): React.ReactElement => {
  const { t } = useI18nNamespace("braze-modal");
  const { registerContext } = useAgenticAiContext();
  const isOperatorChatOpen = useIsAgenticAiChatOpen();
  const canUseOperator = useCanUseOperator();
  // Register through BrazeOperatorContext, not the ambient AgenticAiContext. A modal rendered
  // inside an editor-domain iframe has no AgenticAiContext of its own, so a direct registration
  // resolves to the no-op default and close_modal is dropped without an error anywhere. The
  // context's registerChangeTool routes to whatever the enclosing provider supplies — the
  // iframe bridge there, the host registerTool everywhere else — and already no-ops while the
  // agentic-change gate is off, which is why the effect below no longer checks the gate itself.
  const registerChangeTool = useRegisterChangeTool({ operatorDisabledReason });
  const id = useOperatorEntityId("braze-modal");

  const onOperatorCloseRef = useRef(onOperatorClose ?? toggle);
  onOperatorCloseRef.current = onOperatorClose ?? toggle;

  useEffect(() => {
    if (!isOpen || hideCloseButton) {
      return;
    }

    const cleanup = registerChangeTool({
      toolEntityId: id,
      name: "close_modal",
      description:
        "Close the matching open modal. Identify the target from read_current_page using its modal content and data-operator-id.",
      parameters: z.object({}),
      callback: () => {
        onOperatorCloseRef.current?.();
        return "Modal was successfully closed.";
      },
      options: {
        requiredSkill: "page-controls",
        toolGroup: "Other",
        labels: {
          ready: t("close-modal-ready", "Preparing to close modal"),
          running: t("close-modal-running", "Closing modal"),
          complete: t("close-modal-complete", "Modal closed"),
          failed: t("close-modal-failed", "Failed to close modal"),
          stopped: t("close-modal-stopped", "Stopped closing modal"),
        },
        requiresApproval: "yes",
        argumentsSummaryText: () => operatorLabel,
        renderApproval: () => {
          return (
            <DiffDisplay
              title={t("action-title", "Action")}
              modified={t("close-modal-description", "Close modal: {label}", { label: operatorLabel })}
            />
          );
        },
        toolIcon: <Icon name="times" />,
      },
    });

    return () => {
      cleanup();
    };
  }, [registerChangeTool, id, t, isOpen, operatorLabel, hideCloseButton]);

  useEffect(() => {
    if (!isOpen || !canUseOperator) {
      return;
    }

    return registerContext("braze_modal", "", { ignoreDuplicates: true });
  }, [canUseOperator, isOpen, registerContext]);

  return (
    <StyledBrazeModal
      isOpen={isOpen}
      size={size}
      ignoreOutsideClick={ignoreOutsideClick || isOperatorChatOpen}
      toggle={toggle}
      hideCloseButton={hideCloseButton}
      {...props}
      data-operator-id={id}
    >
      {children}
    </StyledBrazeModal>
  );
};
